import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, interval, of } from 'rxjs';
import { tap, switchMap, startWith } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { SignalRService } from './signalr.service';
import { AuthService } from '../auth/auth.service';
import Swal from 'sweetalert2';

// Interfaces based on API documentation
export interface NotificationDto {
  id: number;
  policyNo: string | null;
  insuredCode: string | null;
  productCode: string | null;
  palnCode: string | null;
  alertMsg: string | null;
  alertToGroup: number | null;
  alertToUser: string | null;
  alertTime: string;  // ISO 8601 format
  url: string | null;
  signRequestNo: string | null;
  menuId: number | null;
  isRead: boolean;
  readTime: string | null;  // ISO 8601 format
  readStatusId: number | null;
}

export interface PaginationInfo {
  skip: number;
  take: number;
  totalCount: number;
  unreadCount: number;
  hasMore: boolean;
}

export interface GetNotificationsResponse {
  success: boolean;
  data: {
    notifications: NotificationDto[];
    pagination: PaginationInfo;
  };
}

export interface UnreadCountResponse {
  success: boolean;
  data: {
    unreadCount: number;
    timestamp: string;
  };
}

export interface MarkReadResponse {
  success: boolean;
  message: string;
  data: {
    notificationId?: number;
    userId?: number;
    timestamp: string;
  };
}

export interface ApiErrorResponse {
  success: false;
  message: string;
  details?: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private apiUrl = `${environment.apiUrl}/noti`;
  
  // BehaviorSubject for real-time unread count updates
  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  // BehaviorSubject for notification list
  private notificationsSubject = new BehaviorSubject<NotificationDto[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();

  // Auto-refresh interval (5 minutes - reduced since SignalR handles real-time)
  private readonly REFRESH_INTERVAL = 300000; // 5 minutes (fallback only)
  
  // Flag to track if SignalR is enabled
  private signalREnabled = false;

  constructor(
    private http: HttpClient,
    private signalRService: SignalRService,
    private authService: AuthService
  ) {
    // Subscribe to SignalR notifications
    this.subscribeToSignalR();
    
    // NOTE: Don't start auto-refresh here - wait until user is authenticated
    // Auto-refresh will be started when initializeSignalR() is called after login
  }
  
  /**
   * Subscribe to SignalR real-time notifications
   */
  private subscribeToSignalR(): void {
    this.signalRService.notification$.subscribe({
      next: (alert) => {
        console.log('[NotificationService] Received SignalR notification:', alert);
        
        // Increment unread count
        const currentCount = this.unreadCountSubject.value;
        this.unreadCountSubject.next(currentCount + 1);
        
        // Refresh notification list to include new notification
        this.refreshNotifications().subscribe({
          error: (err) => console.error('Error refreshing notifications:', err)
        });
        
        // Show toast notification (you can customize this)
        this.showNotificationToast(alert.alertMsg, alert.url);
      },
      error: (err) => {
        console.error('[NotificationService] SignalR error:', err);
      }
    });
    
    // Track SignalR connection state
    this.signalRService.connectionState$.subscribe({
      next: (state) => {
        this.signalREnabled = this.signalRService.isConnected();
        console.log('[NotificationService] SignalR connected:', this.signalREnabled);
      }
    });
  }
  
  /**
   * Show notification toast using SweetAlert2
   */
  private showNotificationToast(message: string, url: string | null): void {
    console.log('[Notification] Showing toast for message:', message);
    
    Swal.fire({
      title: 'ແຈ້ງເຕືອນ',
      text: message,
      icon: 'info',
      toast: true,
      position: 'top-end',
      timer: 30000,
      timerProgressBar: true,
      showConfirmButton: false,
      showCloseButton: true,
      didOpen: (toast) => {
        console.log('[Notification] Toast displayed');
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      }
    }).then(() => {
      console.log('[Notification] Toast closed');
    }).catch((err) => {
      console.error('[Notification] Toast error:', err);
    });
  }
  
  /**
   * Initialize SignalR connection
   * Call this after user login
   */
  public initializeSignalR(userId: string, userTypeId: number): void {
    this.signalRService.startConnection(userId, userTypeId)
      .then(() => {
        console.log('[NotificationService] SignalR initialized successfully');
        // Start auto-refresh now that user is authenticated
        this.startAutoRefresh();
      })
      .catch((err) => {
        console.error('[NotificationService] Failed to initialize SignalR:', err);
        // Still start auto-refresh as fallback even if SignalR fails
        this.startAutoRefresh();
      });
  }
  
  /**
   * Stop SignalR connection
   * Call this on logout
   */
  public stopSignalR(): void {
    this.signalRService.stopConnection()
      .then(() => {
        console.log('[NotificationService] SignalR stopped');
      })
      .catch((err) => {
        console.error('[NotificationService] Error stopping SignalR:', err);
      });
  }

  /**
   * Get user notifications with pagination
   * Automatically uses UserCode and UserTypeId from JWT token if not provided
   * @param userId User ID (optional, defaults to current user's UserCode)
   * @param userTypeId User type ID for filtering (optional, defaults to current user's UserTypeId)
   * @param skip Number of records to skip (default: 0)
   * @param take Number of records to return (default: 20, max: 100)
   * @param skipUpdate If true, don't update the BehaviorSubject (used for pagination)
   */
  getUserNotifications(
    userId?: string,
    userTypeId?: number,
    skip: number = 0,
    take: number = 20,
    skipUpdate: boolean = false
  ): Observable<GetNotificationsResponse> {
    // Get user info from token if not provided
    const decoded = this.authService.decodeToken();
    const effectiveUserId = userId || decoded?.UserCode;
    const effectiveUserTypeId = userTypeId ?? (decoded?.UserTypeId ? parseInt(decoded.UserTypeId) : undefined);
    
    let params = new HttpParams()
      .set('skip', skip.toString())
      .set('take', Math.min(take, 100).toString()); // Max 100 per request

    if (effectiveUserId) {
      params = params.set('userId', effectiveUserId);
    }
    if (effectiveUserTypeId !== undefined && effectiveUserTypeId !== null) {
      params = params.set('userTypeId', effectiveUserTypeId.toString());
    }

    return this.http.get<GetNotificationsResponse>(`${this.apiUrl}/user`, { params }).pipe(
      tap(response => {
        if (response.success && response.data && !skipUpdate) {
          // Update local notifications list
          this.notificationsSubject.next(response.data.notifications);
          // Update unread count
          this.unreadCountSubject.next(response.data.pagination.unreadCount);
        }
      })
    );
  }

  /**
   * Get count of unread notifications
   * Automatically uses UserCode and UserTypeId from JWT token if not provided
   * @param userId User ID (optional, defaults to current user's UserCode)
   * @param userTypeId User type ID for filtering (optional, defaults to current user's UserTypeId)
   */
  getUnreadCount(userId?: string, userTypeId?: number): Observable<UnreadCountResponse> {
    // Get user info from token if not provided
    const decoded = this.authService.decodeToken();
    const effectiveUserId = userId || decoded?.UserCode;
    const effectiveUserTypeId = userTypeId ?? (decoded?.UserTypeId ? parseInt(decoded.UserTypeId) : undefined);
    
    if (!effectiveUserId) {
      console.warn('[NotificationService] No userId available for getUnreadCount');
      // Return empty response instead of making API call
      return of({ success: true, data: { unreadCount: 0, timestamp: new Date().toISOString() } });
    }
    
    let params = new HttpParams();
    params = params.set('userId', effectiveUserId);
    if (effectiveUserTypeId !== undefined && effectiveUserTypeId !== null) {
      params = params.set('userTypeId', effectiveUserTypeId.toString());
    }

    return this.http.get<UnreadCountResponse>(`${this.apiUrl}/unread-count`, { params }).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.unreadCountSubject.next(response.data.unreadCount);
        }
      })
    );
  }

  /**
   * Mark a specific notification as read
   * User ID is automatically extracted from JWT token
   * @param notificationId ID of the notification to mark as read
   */
  markNotificationAsRead(notificationId: number): Observable<MarkReadResponse> {
    return this.http.post<MarkReadResponse>(`${this.apiUrl}/${notificationId}/mark-read`, {}).pipe(
      tap(response => {
        if (response.success) {
          // Update local notification list
          const notifications = this.notificationsSubject.value;
          const updatedNotifications = notifications.map(notif => 
            notif.id === notificationId ? { ...notif, isRead: true, readTime: response.data.timestamp } : notif
          );
          this.notificationsSubject.next(updatedNotifications);
          
          // Decrement unread count
          const currentCount = this.unreadCountSubject.value;
          this.unreadCountSubject.next(Math.max(0, currentCount - 1));
        }
      })
    );
  }

  /**
   * Mark all notifications as read
   * User ID is automatically extracted from JWT token
   */
  markAllNotificationsAsRead(): Observable<MarkReadResponse> {
    return this.http.post<MarkReadResponse>(`${this.apiUrl}/mark-all-read`, {}).pipe(
      tap(response => {
        if (response.success) {
          // Update local notification list
          const notifications = this.notificationsSubject.value;
          const updatedNotifications = notifications.map(notif => ({
            ...notif,
            isRead: true,
            readTime: response.data.timestamp
          }));
          this.notificationsSubject.next(updatedNotifications);
          
          // Reset unread count to 0
          this.unreadCountSubject.next(0);
        }
      })
    );
  }

  /**
   * Load more notifications (for pagination)
   * @param userId User ID (optional)
   * @param userTypeId User type ID (optional)
   * @param skip Number of records to skip
   * @param take Number of records to return
   */
  loadMoreNotifications(
    userId?: string,
    userTypeId?: number,
    skip: number = 0,
    take: number = 20
  ): Observable<GetNotificationsResponse> {
    return this.getUserNotifications(userId, userTypeId, skip, take, true).pipe(
      tap(response => {
        if (response.success && response.data) {
          // Append to existing notifications
          const existingNotifications = this.notificationsSubject.value;
          const newNotifications = [...existingNotifications, ...response.data.notifications];
          this.notificationsSubject.next(newNotifications);
          // Update unread count
          this.unreadCountSubject.next(response.data.pagination.unreadCount);
        }
      })
    );
  }

  /**
   * Refresh notifications (reload from beginning)
   * @param userId User ID (optional)
   * @param userTypeId User type ID (optional)
   */
  refreshNotifications(userId?: string, userTypeId?: number): Observable<GetNotificationsResponse> {
    return this.getUserNotifications(userId, userTypeId, 0, 20);
  }

  // Track if auto-refresh has been started
  private autoRefreshStarted = false;

  /**
   * Start auto-refresh for unread count (every 5 minutes as fallback)
   * Only starts if user is authenticated
   */
  startAutoRefresh(): void {
    // Only start once and only if token exists
    if (this.autoRefreshStarted) {
      return;
    }
    
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('[NotificationService] Skipping auto-refresh - no token');
      return;
    }
    
    this.autoRefreshStarted = true;
    console.log('[NotificationService] Starting auto-refresh for unread count');
    
    interval(this.REFRESH_INTERVAL)
      .pipe(
        startWith(0), // Trigger immediately on subscription
        switchMap(() => {
          // Double-check token still exists before each refresh
          const currentToken = localStorage.getItem('token');
          if (!currentToken) {
            return [];
          }
          return this.getUnreadCount();
        })
      )
      .subscribe({
        error: (err) => {
          console.error('Error auto-refreshing unread count:', err);
        }
      });
  }

  /**
   * Get current unread count value (synchronous)
   */
  getCurrentUnreadCount(): number {
    return this.unreadCountSubject.value;
  }

  /**
   * Get current notifications value (synchronous)
   */
  getCurrentNotifications(): NotificationDto[] {
    return this.notificationsSubject.value;
  }

  /**
   * Clear all local notifications
   */
  clearNotifications(): void {
    this.notificationsSubject.next([]);
  }

  /**
   * Check API health
   */
  checkHealth(): Observable<any> {
    return this.http.get(`${this.apiUrl}/health`);
  }
}
